import React from "react";
import "./StartGuide.css";

const StartGuide = () => {
  return (
    <div className="quick-start-guide">
      <h1>Quick Start Guide</h1>
      <p>Deploy and integrate a powerful chatbot on your website within minutes using <strong>XenaChat</strong>.</p>

      <h2>Step 1 – Upload Knowledge Base and Help Content</h2>
      <p>Sign in to your <strong>XenaChat</strong> account and navigate to <strong>[Build Your AI Chatbot]</strong> in the left-hand menu to access the Dashboard.</p>
      <ul>
        <li>For Creating a new <strong>[Bot]</strong>.</li>
        <li>Provide a your Full Name, Your Role, and other input fields and click on <strong>[Next]</strong>Button.</li>
        <li>Then provide Company Name, Select Employees Number, Your Industry, and your Department and finally click on <strong>[Next]</strong> Button.</li>
        <li>Then update the status of your organization to reflect what it is currently doing and click on <strong>[Next]</strong> Button.</li>
        <li>Now provide the name of the Bot which you want to use and click on <strong>[Next]</strong> Button.</li>
      </ul>

      <h3>Uploading Files</h3>
      <ul>
        <li>Under the <strong>[Files]</strong> section, drag and drop your documents or select them manually. Multiple files can be uploaded simultaneously.</li>
        <li>You can also add company website, Apps, etc to train you model.</li>
        <li>Click on the <strong>[Next]</strong> Button to initiate training.</li>
      </ul>

      <h3>Upload Bot Image and accent Color</h3>
      <ul>
        <li>Select the <strong>[Upload Image]</strong> and to upload the image of your bot.</li>
        <li>Choose <strong>[Accent ]</strong>color.</li>
        <li>Click <strong>[Submit]</strong> to all information.</li>
      </ul>

      <p><strong>Note:</strong> All uploaded files and links are listed under <strong>SOURCES &gt; LINKS</strong>. You can monitor their processing status, and configure file settings or enable auto-sync for web-based content.</p>

      <h2>Step 2 – Configure Bot Appearance</h2>
      <p>Customize your bot’s presentation by navigating to the Your Bots and <strong>[Appearance]</strong> tab. Follow this step-by-step guide to configure your chatbot’s visual elements.</p>
      <ul>
        <li><strong>Bot Widget Name on Widget:</strong> Enter your Bot’s widget name.</li>
        <li><strong>Subheading:</strong> Define a brief description or tagline for the bot.</li>
        <li><strong>Bot Logo:</strong> Upload your Bot logo.</li>
        <li><strong>Bot Avatar:</strong> Choose a representative avatar for the chatbot.</li>
        <li><strong>Accent Color:</strong> Select your preferred color theme.</li>
        <li><strong>Widget Icon:</strong> Pick an icon for the chat widget.</li>
        <li><strong>Widget Position:</strong> Choose left or right placement on your website.</li>
        <li><strong>Input Placeholder:</strong> Set the chat input placeholder</li>
        <li><strong>Hide XenaChat Branding Image:</strong> This option can be enabled from the Manage Plans section.</li>
        <li><strong>Default Widget State:</strong> Set the widget to open or closed on page load.</li>
        <li><strong>Show Starter Questions:</strong> If enabled then it will show the starter quesitons on the Chatbot.</li>

      </ul>
      <p>Ensure all changes are saved by clicking <strong>SAVE</strong>.</p>

      <h2>Step 3 – Customize Your Bot Settings</h2>
      <p>
        Configure and fine-tune your bot’s behavior, appearance, and response settings before deployment.
      </p>

      <ul>
        <li>
          <strong>AI Model: </strong>
          Select the AI model your bot will use. Free tier users have limited access,
          while Starter, Professional, and Advanced plans unlock additional model options.
        </li>

        <li>
          <strong>Conversational Mode: </strong>
          Enable this option to break responses into shorter, more readable messages.
        </li>

        <li>
          <strong>Strict FAQ Responses: </strong>
          When enabled, the bot will respond only with exact content from your FAQ knowledge base.
        </li>

        <li>
          <strong>Response Length: </strong>
          Choose the preferred length of responses (Short, Medium, or Long).
        </li>

        <li>
          <strong>Suggest Follow-Up Questions: </strong>
          Enable this to automatically suggest two related follow-up questions after each response.
        </li>

        <li>
          <strong>Allow Image Usage: </strong>
          Allows the bot to use uploaded or scraped images for training and response generation.
        </li>

        <li>
          <strong>Show Sources with Responses: </strong>
          Toggle this setting to display or hide sources alongside responses.
        </li>

        <li>
          <strong>Post-Chat Feedback: </strong>
          Enable or disable the option for users to provide feedback after a chat session.
        </li>

        <li>
          <strong>Multilingual Support: </strong>
          Enable to allow the bot to respond in multiple languages. Disable to restrict responses to the selected language.
        </li>

        <li>
          <strong>Welcome Message: </strong>
          Customize the greeting message displayed when a user starts a conversation.
          Example: “Hey there, how can I help you?”
        </li>

        <li>
          <strong>Message When No Source is Added: </strong>
          Set the message displayed if the bot has not yet been trained.
          Example: “The bot is yet to be trained...”
        </li>

        <li>
          <strong>Server Error Message: </strong>
          Customize the message shown when a server error occurs.
          Example: “Apologies, there seems to be a server error.”
        </li>

        <li>
          <strong>Save or Discard Changes: </strong>
          After making updates, click <strong>Save Changes</strong> to apply settings
          or <strong>Discard Changes</strong> to cancel modifications.
        </li>
      </ul>

      <h2>Step 4 – Configure Advanced Bot Settings</h2>

      <p>
        Customize user nudges, email notifications, usage limits, data collection, and working hours
        to optimize your bot’s performance and security.
      </p>

      <h3>4.1 Starter Questions (User Nudges)</h3>
      <p>
        These questions are displayed upfront to guide users when they start a conversation.
        You can style the answers using Markdown formatting.
      </p>
      <ul>
        <li>Click <strong>Add New Starter Question</strong>.</li>
        <li>Enter the question you want to display as a suggested prompt.</li>
        <li>Save changes to apply.</li>
      </ul>

      <h3>4.2 Email Transcript & Support Configuration</h3>
      <ul>
        <li>
          <strong>Send Email Transcript: </strong>
          Enable to automatically email users their chat transcript after the chat ends.
        </li>

        <li>
          <strong>Main Customer Support Email Address: </strong>
          Set a general support email (e.g., support@example.com) to receive inquiries.
        </li>

        <li>
          <strong>Add Support Emails: </strong>
          Redirect specific issues to designated email addresses.
        </li>

        <li>
          Click <strong>Add New</strong> to include additional support emails.
        </li>

        <li>
          Click <strong>Save Changes</strong> to apply settings or <strong>Discard Changes</strong> to cancel.
        </li>
      </ul>

      <h3>4.3 Usage & Security Settings</h3>
      <ul>
        <li>
          <strong>Usage Limit Per User: </strong>
          Set the maximum number of messages allowed per user (applies to website widget,
          WhatsApp, Messenger, and Telegram).
        </li>

        <li>
          <strong>Messages Per User: </strong>
          Define the daily message limit.
        </li>

        <li>
          <strong>User Limit Warning: </strong>
          Customize the message shown when a user exceeds the allowed limit.
        </li>

        <li>
          <strong>Configure Bot Rate Limit: </strong>
          Set the API rate limit per minute (maximum 300 requests per minute).
        </li>

        <li>
          <strong>Access for Users with Masked IPs: </strong>
          Enable to block users accessing the chatbot via masked or hidden IP addresses.
        </li>

        <li>
          Click <strong>Save Changes</strong> to apply settings or <strong>Discard Changes</strong> to cancel.
        </li>
      </ul>

      <h3>4.4 User Data Collection</h3>
      <ul>
        <li>
          <strong>Enable User Form: </strong>
          Enable or disable a form to collect user information before chat begins.
        </li>

        <li>
          <strong>Enable Captcha: </strong>
          Prevent spam and automated bot abuse.
        </li>

        <li>
          <strong>Add Question: </strong>
          Add custom questions to collect specific user data.
        </li>

        <li>
          Click <strong>Save Changes</strong> or <strong>Discard Changes</strong> as needed.
        </li>
      </ul>

      <h3>4.5 Working Hours Configuration</h3>
      <ul>
        <li>
          <strong>Enable Working Hours: </strong>
          Define specific days and times when the bot will be active.
        </li>

        <li>
          Save changes to apply your schedule or discard to cancel modifications.
        </li>
      </ul>


      <h2>Step 5 – Embed the Bot on Your Website</h2>
      <ul>
        <li>Navigate to the <strong>Connect</strong> tab.</li>

        <li>
          Select one of the following integration options:
          <strong>JS Snippet</strong>, <strong>iFrame Snippet</strong>, or <strong>REST API</strong>,
          and copy the generated code.
        </li>

        <li>
          For <strong>JS Snippet</strong> or <strong>iFrame Snippet</strong>,
          paste the code into your website’s HTML where you want the chatbot to appear.
        </li>

        <li>
          For <strong>REST API</strong>, follow the integration instructions provided
          in the documentation to connect the chatbot to your application or platform.
        </li>
      </ul>


      <p>To further optimize your bot, refer to the guide on configuring <strong>XenaChat</strong> settings for advanced customization.</p>
    </div>
  );
};

export default StartGuide;
